import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { AlertTriangle, Loader2 } from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDeleteAutomationServer } from "@/hooks/useAutomationServersQuery";

interface DeleteAutomationServerModalProps {
  children: React.ReactNode;
  serverName: string;
  serverId: string;
  // Cloud (hetzner) servers are deprovisioned — that's a "Delete". Bring-your-own
  // servers are only detached from this operations center — the machine keeps
  // running — so for those it's a "Disconnect".
  isCloud?: boolean;
  onDelete?: () => void;
}

export function DeleteAutomationServerModal({
  children,
  serverName,
  serverId,
  isCloud = false,
  onDelete,
}: DeleteAutomationServerModalProps) {
  const [confirmationName, setConfirmationName] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const deleteMutation = useDeleteAutomationServer();

  const verb = isCloud ? "Delete" : "Disconnect";
  const verbing = isCloud ? "Deleting" : "Disconnecting";

  const isNameValid = confirmationName === serverName;

  const handleDelete = async () => {
    if (!isNameValid) return;

    try {
      await deleteMutation.mutateAsync(serverId);

      // Close modal and redirect
      setOpen(false);
      navigate("/automation-servers");
      onDelete?.();
    } catch (error: any) {
      console.error(`Error ${verbing.toLowerCase()} automation server:`, error);

      let errorMessage = `Failed to ${verb.toLowerCase()} automation server`;

      if (error?.response?.status === 403) {
        errorMessage = `You don't have permission to ${verb.toLowerCase()} this automation server. Only admin users can.`;
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      alert(errorMessage);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setConfirmationName("");
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent className="sm:max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle
            className={`flex items-center gap-2 ${isCloud ? "text-red-600" : "text-amber-600"}`}
          >
            <AlertTriangle className="h-5 w-5" />
            {verb} Automation Server
          </AlertDialogTitle>
          <AlertDialogDescription>
            {isCloud ? (
              <>
                This action cannot be undone. This will permanently delete the
                automation server <strong>&quot;{serverName}&quot;</strong> and all
                its associated data.
              </>
            ) : (
              <>
                This will disconnect <strong>&quot;{serverName}&quot;</strong> from
                this operations center and remove its record here. The server itself keeps running and can be
                re-registered later.
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="confirmation-name" className="text-sm font-medium">
              To confirm, type the server name: <strong>&quot;{serverName}&quot;</strong>
            </label>
            <Input
              id="confirmation-name"
              placeholder={serverName}
              value={confirmationName}
              onChange={(e) => setConfirmationName(e.target.value)}
              className={confirmationName && !isNameValid ? "border-red-500" : ""}
            />
            {confirmationName && !isNameValid && (
              <p className="text-sm text-red-600">
                Server name does not match. Please enter the exact name.
              </p>
            )}
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={!isNameValid || deleteMutation.isPending}
            className={
              isCloud
                ? "bg-red-600 hover:bg-red-700 focus:ring-red-600"
                : "bg-amber-600 hover:bg-amber-700 focus:ring-amber-600"
            }
          >
            {deleteMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {verbing}...
              </>
            ) : (
              `${verb} Server`
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
